import { Injectable, computed, inject, signal } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, catchError, of, tap, throwError, timer } from 'rxjs';
import { retry } from 'rxjs/operators';
import { environment } from '@env/environment';
import { Vehicle } from '../models/vehicle.model';

export type VehiclesLoadStatus =
  'idle' | 'loading' | 'loaded' | 'unauthenticated' | 'not-found' | 'unavailable' | 'error';

const RETRYABLE_STATUS = 503;
const MAX_RETRIES = 3;

@Injectable({ providedIn: 'root' })
export class VehiclesService {
  private readonly http = inject(HttpClient);
  private readonly endpoint = `${environment.apiUrl}/api/customers/me/vehicles`;

  private readonly _vehicles = signal<Vehicle[]>([]);
  private readonly _status = signal<VehiclesLoadStatus>('idle');

  readonly vehicles = this._vehicles.asReadonly();
  readonly status = this._status.asReadonly();
  readonly totalVehicles = computed(() => this._vehicles().length);
  readonly activeDevices = computed(
    () => this._vehicles().filter((v) => v.status === 'ACTIVA').length
  );

  loadVehicles(): Observable<Vehicle[] | null> {
    this._status.set('loading');

    return this.http.get<Vehicle[]>(this.endpoint).pipe(
      retry({
        count: MAX_RETRIES,
        delay: (error, retryIndex) => {
          if (error instanceof HttpErrorResponse && error.status === RETRYABLE_STATUS) {
            return timer(500 * Math.pow(2, retryIndex - 1));
          }
          return throwError(() => error);
        },
      }),
      tap((vehicles) => {
        this._vehicles.set(vehicles);
        this._status.set('loaded');
      }),
      catchError((error: unknown) => {
        this._vehicles.set([]);
        this._status.set(this.mapErrorStatus(error));
        return of(null);
      })
    );
  }

  /**
   * Vehículos ya cargados o, si aún no lo están, los carga. `null` si la carga
   * falla (mismo contrato que `loadVehicles`). Pensado para guards.
   */
  ensureLoaded(): Observable<Vehicle[] | null> {
    return this._status() === 'loaded' ? of(this._vehicles()) : this.loadVehicles();
  }

  reset(): void {
    this._vehicles.set([]);
    this._status.set('idle');
  }

  private mapErrorStatus(error: unknown): VehiclesLoadStatus {
    if (!(error instanceof HttpErrorResponse)) return 'error';
    switch (error.status) {
      case 401:
        return 'unauthenticated';
      case 404:
        return 'not-found';
      case 503:
        return 'unavailable';
      default:
        return 'error';
    }
  }
}
